import React, { useEffect, useState } from "react";
import api, { formatPrice, API } from "@/lib/api";
import { Calculator, FileDown, Sparkles, Loader2, Globe, TrendingUp, TrendingDown, Minus, History, ChevronRight, Settings } from "lucide-react";
import { ValuationBrandSettings } from "./ValuationBrandSettings";

const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-ink-50 focus:border-gold outline-none";
const PROPERTY_TYPES = ["Appartamento", "Villa", "Attico", "Loft", "Casa indipendente", "Bilocale", "Trilocale", "Uffici", "Locali commerciali"];
const CONDITIONS = ["Nuova costruzione", "Ottimo stato", "Buono stato", "Da ristrutturare"];

const emptyForm = () => ({
    address: "", city: "", sqm: "",
    property_type: "Appartamento", condition: "Buono stato",
    floor: "", has_elevator: false, has_garden: false, has_parking: false,
    notes: "",
});

export default function CrmValuation() {
    const [f, setF] = useState(emptyForm());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [res, setRes] = useState(null);
    const [history, setHistory] = useState([]);
    const [brandOpen, setBrandOpen] = useState(false);

    const loadHistory = async () => {
        try {
            const r = await api.get("/crm/valuation/list", { params: { limit: 20 } });
            setHistory(r.data.items || []);
        } catch (_e) { /* silent — mostra vuoto */ }
    };
    useEffect(() => { loadHistory(); }, []);

    const canGenerate = f.address.trim().length >= 3 && f.city.trim().length >= 2 && parseFloat(f.sqm) > 10 && !busy;

    const run = async () => {
        if (!canGenerate) return;
        setBusy(true); setError(null); setRes(null);
        try {
            const body = { ...f, sqm: parseFloat(f.sqm) };
            if (body.floor === "") delete body.floor; else body.floor = parseInt(body.floor, 10);
            const r = await api.post("/crm/valuation/ai-generate", body);
            setRes(r.data);
            loadHistory();
        } catch (e) {
            setError(e?.response?.data?.detail || "Errore durante la valutazione");
        } finally { setBusy(false); }
    };

    const openHistoryItem = async (id) => {
        try {
            const r = await api.get(`/crm/valuation/${id}`);
            setRes(r.data);
            const inp = r.data.input || {};
            setF({
                ...emptyForm(),
                address: inp.address || "",
                city: inp.city || "",
                sqm: inp.sqm != null ? String(inp.sqm) : "",
                property_type: inp.property_type || "Appartamento",
                condition: inp.condition || "Buono stato",
                floor: inp.floor != null ? String(inp.floor) : "",
                has_elevator: !!inp.has_elevator,
                has_garden: !!inp.has_garden,
                has_parking: !!inp.has_parking,
                notes: inp.notes || "",
            });
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (_e) { /* noop */ }
    };

    const resetForm = () => { setF(emptyForm()); setRes(null); setError(null); };

    return (
        <div className="space-y-4">
            <div className="flex justify-end"><button data-testid="valuation-brand-settings-button" type="button" onClick={() => setBrandOpen(!brandOpen)} className="cta-outline px-3 py-2 text-xs flex items-center gap-2"><Settings size={14} /> Impostazioni PDF</button></div>
            {brandOpen && <ValuationBrandSettings onClose={() => setBrandOpen(false)} />}
        <div data-testid="valuation-tab" className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* === LEFT: FORM === */}
            <div className="lg:col-span-2 space-y-4">
                <div className="border border-white/10 bg-ink-900/40 p-6 space-y-4">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 font-serif text-xl text-ink-50">
                            <Calculator size={18} className="text-gold" /> Valuta un immobile
                        </div>
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-gold border border-gold/40 px-2 py-1">
                            <Sparkles size={11} /> AI + Ricerca web
                        </span>
                    </div>
                    <p className="text-xs text-ink-400">
                        Inserisci indirizzo, mq e stato: l&apos;IA analizza in tempo reale i prezzi reali dei portali (idealista, immobiliare.it, casa.it) e i dati OMI di zona per generare la stima e il report PDF per il proprietario.
                    </p>

                    <div>
                        <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Indirizzo *</label>
                        <input data-testid="val-address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Via Dante 12, Milano" className={inputCls} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Città *</label>
                            <input data-testid="val-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="Milano" className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Metri quadri *</label>
                            <input data-testid="val-sqm" type="number" min="11" value={f.sqm} onChange={(e) => setF({ ...f, sqm: e.target.value })} className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Tipologia</label>
                            <select data-testid="val-type" value={f.property_type} onChange={(e) => setF({ ...f, property_type: e.target.value })} className="w-full bg-ink-950 border border-white/15 px-3 py-2.5 text-sm text-ink-50 outline-none">
                                {PROPERTY_TYPES.map((t) => <option key={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Stato</label>
                            <select data-testid="val-condition" value={f.condition} onChange={(e) => setF({ ...f, condition: e.target.value })} className="w-full bg-ink-950 border border-white/15 px-3 py-2.5 text-sm text-ink-50 outline-none">
                                {CONDITIONS.map((t) => <option key={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Piano</label>
                            <input value={f.floor} onChange={(e) => setF({ ...f, floor: e.target.value })} placeholder="es. 3" className={inputCls} />
                        </div>
                        <div className="flex flex-col gap-1 text-[11px] text-ink-100 mt-5">
                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={f.has_elevator} onChange={(e) => setF({ ...f, has_elevator: e.target.checked })} /> Ascensore</label>
                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={f.has_garden} onChange={(e) => setF({ ...f, has_garden: e.target.checked })} /> Giardino</label>
                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={f.has_parking} onChange={(e) => setF({ ...f, has_parking: e.target.checked })} /> Box/Parcheggio</label>
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Note (opzionale)</label>
                        <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} placeholder="Vista panoramica, ristrutturazione recente, terrazzo di 20m²..." className={inputCls} />
                    </div>
                    <div className="flex gap-2">
                        <button data-testid="val-run" onClick={run} disabled={!canGenerate} className="cta-gold flex-1 py-3 text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2">
                            {busy ? <><Loader2 size={14} className="animate-spin" /> Analisi in corso (~30-60s)...</> : <><Sparkles size={14} /> Genera Valutazione AI</>}
                        </button>
                        {res && <button onClick={resetForm} className="cta-outline px-4 py-3 text-xs">Nuova</button>}
                    </div>
                    {error && <div data-testid="val-error" className="border border-red-700/40 bg-red-900/20 text-red-300 text-xs p-3">{error}</div>}
                    <div className="text-[10px] text-ink-400 flex items-center gap-1.5"><Globe size={10} /> Ogni valutazione consuma crediti AI (chiamata esplicita, non automatica)</div>
                </div>

                {/* Storico */}
                {history.length > 0 && (
                    <div className="border border-white/10 bg-ink-900/30 p-5">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-ink-400 mb-3"><History size={12} /> Ultime valutazioni</div>
                        <div className="space-y-1.5 max-h-72 overflow-y-auto">
                            {history.map((h) => (
                                <button key={h.id} data-testid={`val-history-${h.id}`} onClick={() => openHistoryItem(h.id)} className="w-full text-left px-2 py-2 border border-white/5 hover:border-gold/40 flex items-center justify-between gap-2 text-xs">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-ink-100 truncate">{h.input?.address}, {h.input?.city}</div>
                                        <div className="text-[10px] text-ink-500">{h.input?.sqm} m² · {new Date(h.created_at).toLocaleDateString("it-IT")}</div>
                                    </div>
                                    <div className="text-price text-[11px] shrink-0">{h.ai_result?.price_range?.mid ? formatPrice(h.ai_result.price_range.mid) : "—"}</div>
                                    <ChevronRight size={12} className="text-ink-400 shrink-0" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* === RIGHT: RESULT === */}
            <div className="lg:col-span-3">
                {!res && !busy && (
                    <div className="border border-white/10 bg-ink-900/20 p-10 text-center text-sm text-ink-500">
                        <Calculator size={32} className="mx-auto text-gold/40 mb-4" />
                        Compila i dati dell&apos;immobile e clicca <strong className="text-ink-100">Genera Valutazione AI</strong>. L&apos;analisi utilizza dati reali dai portali immobiliari + OMI di zona.
                    </div>
                )}
                {busy && (
                    <div className="border border-gold/30 bg-gold/5 p-10 text-center text-sm text-ink-100">
                        <Loader2 size={32} className="mx-auto text-gold animate-spin mb-4" />
                        <div className="font-serif text-lg text-ink-50 mb-2">Analisi di mercato in corso...</div>
                        <div className="text-xs text-ink-400 space-y-1">
                            <div>1. Ricerca web sui portali immobiliari</div>
                            <div>2. Recupero quotazioni OMI</div>
                            <div>3. AI Gemini elabora il report</div>
                        </div>
                    </div>
                )}
                {res && !busy && <ValuationResult res={res} />}
            </div>
        </div>
        </div>
    );
}

function TrendIcon({ trend }) {
    if (trend === "in crescita") return <TrendingUp size={11} className="text-green-400" />;
    if (trend === "in calo") return <TrendingDown size={11} className="text-red-400" />;
    return <Minus size={11} className="text-ink-400" />;
}

function ValuationResult({ res }) {
    const ai = res.ai_result || {};
    const grounding = res.grounding || {};
    const groundingOk = !!grounding.ok;
    const pr = ai.price_range || {};
    const comps = ai.comparables || [];
    const omi = ai.omi_zone || {};
    const conf = ai.confidence || "media";
    const confColor = conf === "alta" ? "text-green-400 border-green-500/40" : conf === "bassa" ? "text-amber-400 border-amber-500/40" : "text-gold border-gold/40";
    const pdfUrl = `${API}/crm/valuation/${res.id}/pdf`;
    return (
        <div data-testid="val-result" className="space-y-5">
            {!groundingOk && (
                <div data-testid="val-grounding-warning" className="border border-amber-500/40 bg-amber-900/20 p-3 text-xs text-amber-100 flex items-start gap-2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 mt-0.5"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                    <div>
                        <strong className="text-amber-200">Grounding web non disponibile</strong> — la ricerca sui portali immobiliari non è riuscita per questa valutazione. La stima si basa esclusivamente sulla conoscenza generale del mercato dell&apos;AI. I comparabili e i dati OMI eventualmente indicati NON sono stati verificati contro fonti esterne. Affidabilità impostata su &ldquo;bassa&rdquo;.
                    </div>
                </div>
            )}
            {/* PRICE BANNER */}
            <div className="border border-gold/40 bg-gradient-to-br from-gold/10 to-gold/5 p-8 text-center">
                <div className="text-[10px] uppercase tracking-widest text-ink-400 mb-2">Range di prezzo stimato</div>
                <div className="font-serif text-4xl sm:text-5xl text-gold">
                    € {(pr.min || 0).toLocaleString("it-IT")} <span className="text-ink-400 text-2xl">—</span> € {(pr.max || 0).toLocaleString("it-IT")}
                </div>
                {pr.mid ? (
                    <div className="mt-3 text-sm text-ink-100">
                        Valore centrale: <span className="text-price font-medium">€ {pr.mid.toLocaleString("it-IT")}</span>
                        {pr.price_per_sqm_mid ? <span className="text-ink-400"> · € {pr.price_per_sqm_mid.toLocaleString("it-IT")}/m²</span> : null}
                    </div>
                ) : null}
                <div className="mt-4 flex items-center justify-center gap-3 text-[10px] uppercase tracking-wider">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 border ${confColor}`} data-testid="val-confidence">Affidabilità: {conf}</span>
                    {ai.market_trend && <span className="inline-flex items-center gap-1 px-2 py-1 border border-white/20 text-ink-100"><TrendIcon trend={ai.market_trend} /> Trend {ai.market_trend}</span>}
                </div>
                <a data-testid="val-pdf" href={pdfUrl} target="_blank" rel="noreferrer" className="cta-gold px-5 py-2.5 text-xs mt-6 inline-flex items-center gap-2">
                    <FileDown size={14} /> Scarica Report PDF
                </a>
            </div>

            {/* COMPARABLES */}
            {comps.length > 0 && (
                <div data-testid="val-comparables" className="border border-white/10">
                    <div className="px-4 py-3 text-[10px] uppercase tracking-widest text-ink-400 bg-white/5 flex items-center gap-2">
                        <Globe size={11} /> Comparabili di mercato ({comps.length})
                    </div>
                    <div className="divide-y divide-white/5">
                        {comps.map((c, i) => (
                            <div key={i} className="p-3 grid grid-cols-12 gap-2 items-center text-xs">
                                <div className="col-span-5">
                                    <div className="text-ink-100 truncate">{c.title || "—"}</div>
                                    <div className="text-[10px] text-ink-500 mt-0.5">{c.source} {c.distance_hint ? `· ${c.distance_hint}` : ""}</div>
                                </div>
                                <div className="col-span-2 text-ink-400 text-right">{c.sqm ? `${c.sqm} m²` : "—"}</div>
                                <div className="col-span-2 text-ink-100 text-right">{c.price ? `€ ${c.price.toLocaleString("it-IT")}` : "—"}</div>
                                <div className="col-span-2 text-price text-right">{c.price_per_sqm ? `€ ${c.price_per_sqm.toLocaleString("it-IT")}/m²` : "—"}</div>
                                <div className="col-span-1 text-right">
                                    {c.url && <a href={c.url} target="_blank" rel="noreferrer" className="text-ink-400 hover:text-gold" title="Apri fonte"><Globe size={12} /></a>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* OMI ZONE */}
            {omi && (omi.zone_name || omi.min_per_sqm) && (
                <div data-testid="val-omi" className="border border-white/10 p-5">
                    <div className="text-[10px] uppercase tracking-widest text-ink-400 mb-3">Dati OMI di Zona (Agenzia Entrate)</div>
                    {omi.zone_name && <div className="text-sm text-ink-100 mb-2">Zona: <span className="text-gold">{omi.zone_name}</span></div>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        {(omi.min_per_sqm || omi.max_per_sqm) && (
                            <div className="border border-white/10 p-3">
                                <div className="text-[10px] text-ink-400 uppercase mb-1">Range €/m²</div>
                                <div className="text-ink-100">€ {omi.min_per_sqm?.toLocaleString("it-IT") || "—"} — € {omi.max_per_sqm?.toLocaleString("it-IT") || "—"}</div>
                            </div>
                        )}
                        {omi.condition_new && (
                            <div className="border border-white/10 p-3">
                                <div className="text-[10px] text-ink-400 uppercase mb-1">Nuova costruzione</div>
                                <div className="text-ink-100">€ {omi.condition_new.min?.toLocaleString("it-IT")} — € {omi.condition_new.max?.toLocaleString("it-IT")}/m²</div>
                            </div>
                        )}
                        {omi.condition_used && (
                            <div className="border border-white/10 p-3">
                                <div className="text-[10px] text-ink-400 uppercase mb-1">Usato / buono stato</div>
                                <div className="text-ink-100">€ {omi.condition_used.min?.toLocaleString("it-IT")} — € {omi.condition_used.max?.toLocaleString("it-IT")}/m²</div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* NARRATIVE */}
            {ai.narrative && (
                <div data-testid="val-narrative" className="border border-white/10 p-5">
                    <div className="text-[10px] uppercase tracking-widest text-ink-400 mb-3 flex items-center gap-1.5"><Sparkles size={11} /> Analisi AI</div>
                    <div className="text-sm text-ink-100 whitespace-pre-wrap leading-relaxed">{ai.narrative}</div>
                </div>
            )}

            {ai.assumptions?.length > 0 && (
                <div className="text-[11px] text-ink-500 italic border-l-2 border-gold/40 pl-3">
                    <div className="font-medium text-ink-400 mb-1">Assunzioni:</div>
                    <ul className="list-disc list-inside space-y-0.5">
                        {ai.assumptions.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                </div>
            )}
        </div>
    );
}
